import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { errorMessage } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { openDocument } from "@/features/documents/queries";
import { Button } from "@/shared/ui/button";

/** Open an authorized file, retaining an exact evidence revision when supplied. */
export function DocumentFileLink({
  id,
  versionId,
  label,
}: {
  id: string;
  versionId?: string | null;
  label?: string;
}): ReactElement | null {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  if (!can("documents.document.view")) return null;
  const open = async (): Promise<void> => {
    setPending(true);
    setError(undefined);
    try {
      await openDocument(id, versionId ?? undefined);
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setPending(false);
    }
  };
  return (
    <span className="inline-flex flex-col items-start gap-1">
      <Button type="button" size="sm" variant="link" disabled={pending} onClick={() => void open()}>
        {label ?? t("fileEvidence.openFile")}
      </Button>
      {error ? (
        <span role="alert" className="text-sm text-destructive">
          {error}
        </span>
      ) : null}
    </span>
  );
}
